import { useEffect, useState } from "react";
import { Search, Plus, Trash2, Loader2, ShieldAlert, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import ConsumerDashboard from "@/components/app/ConsumerDashboard";

const STATUSES = ["New Intake", "Active Disputes", "Under Bureau Review", "Resolved / Blocked"];
const EMPTY = { fullName: "", email: "", phone: "", address: "", ssnLast4: "", dob: "", ftcReportId: "", policeReportNumber: "", status: "New Intake", notes: "" };

export default function ConsumersCRM() {
  const { apiErr, user } = useAuth();
  const isViewOnly = user?.permission === "view";
  const isOwner = user?.role !== "staff";
  const [team, setTeam] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [assignee, setAssignee] = useState("");
  const [shareWith, setShareWith] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/consumers", { params: { search: search || undefined, status: statusFilter } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, statusFilter]);
  useEffect(() => { if (user && user.role !== "staff") api.get("/staff").then(({ data }) => setTeam(data.members || [])).catch(() => {}); }, [user]);

  const create = async () => {
    if (!form.fullName.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      await api.post("/consumers", form);
      toast.success("Consumer added");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openDetail = (id) => setActiveId(id);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this consumer and all their disputes?")) return;
    await api.delete(`/consumers/${id}`);
    toast.success("Consumer removed"); load();
  };

  const openAssign = (c, e) => { e.stopPropagation(); setAssignFor(c); setAssignee(c.created_by || user?.id || ""); setShareWith(c.shared_with || []); };
  const toggleShare = (id) => setShareWith((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const saveAssign = async () => {
    try {
      await api.put(`/consumers/${assignFor.id}/assign`, { assigneeId: assignee, sharedWith: shareWith.filter((x) => x !== assignee) });
      toast.success("Client reassigned"); setAssignFor(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (activeId) return <ConsumerDashboard consumerId={activeId} onBack={() => { setActiveId(null); load(); }} />;

  return (
    <div className="space-y-6" data-testid="crm-panel">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search consumers…"
              className="pl-9 bg-[#FFFFFF] border-neutral-200" data-testid="consumer-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-[#FFFFFF] border-neutral-200" data-testid="consumer-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="add-consumer-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add Consumer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6EEEE] border-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">New Consumer</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <Field label="Full name *" v={form.fullName} onC={(v) => setForm({ ...form, fullName: v })} testid="consumer-fullname-input" span />
              <Field label="Email" v={form.email} onC={(v) => setForm({ ...form, email: v })} />
              <Field label="Phone" v={form.phone} onC={(v) => setForm({ ...form, phone: v })} />
              <Field label="Address" v={form.address} onC={(v) => setForm({ ...form, address: v })} span />
              <Field label="SSN (last 4)" v={form.ssnLast4} onC={(v) => setForm({ ...form, ssnLast4: v })} />
              <Field label="Date of birth" v={form.dob} onC={(v) => setForm({ ...form, dob: v })} placeholder="1990-01-01" />
              <Field label="FTC report #" v={form.ftcReportId} onC={(v) => setForm({ ...form, ftcReportId: v })} />
              <Field label="Police report #" v={form.policeReportNumber} onC={(v) => setForm({ ...form, policeReportNumber: v })} />
              <div className="col-span-2">
                <Label className="text-neutral-600">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-neutral-600">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="save-consumer-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Consumer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-neutral-400">No consumers yet. Add your first client.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} onClick={() => openDetail(c.id)}
              className="group cursor-pointer rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-5 hover:border-[#C1121F]/40 transition-colors"
              data-testid="consumer-card-item">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-xl bg-[#C1121F]/15 grid place-items-center font-display font-bold text-[#9B0E18]">
                    {c.fullName.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </span>
                  <div>
                    <div className="font-semibold">{c.fullName}</div>
                    <div className="text-xs text-neutral-400">{c.email || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isOwner && (
                    <button onClick={(e) => openAssign(c, e)} className="text-neutral-400 hover:text-[#C1121F]" data-testid="card-assign-button" title="Assign">
                      <UserCog className="h-4 w-4" />
                    </button>
                  )}
                  {!isViewOnly && (
                    <button onClick={(e) => remove(c.id, e)} className="text-neutral-400 hover:text-rose-400" data-testid="delete-consumer-button">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-md bg-neutral-50 border border-neutral-200 text-neutral-600">SSN •{c.ssnLast4 || "----"}</span>
                {c.ftcReportId && <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> FTC</span>}
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-neutral-500">{c.accountsCount} disputes</span>
                <span className="font-mono text-[#9B0E18]">{c.totalDisputedAmount}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-[11px] text-neutral-400">{c.status}</span>
                {c.assigneeName && <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#C1121F]/[0.06] border border-[#C1121F]/20 text-[#9B0E18]" data-testid="card-assignee-badge">{c.assigneeName}</span>}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2">
                {c.readyToFile
                  ? <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-200 text-emerald-700" data-testid="card-readiness-badge">Ready to file</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-200 text-amber-700" data-testid="card-readiness-badge">Docs pending</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="bg-[#F6EEEE] border-neutral-200">
          <DialogHeader><DialogTitle className="font-display">Assign {assignFor?.fullName}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-neutral-600">Primary owner</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger className="mt-1.5 bg-white border-neutral-200" data-testid="card-assign-owner-select"><SelectValue placeholder="Select teammate" /></SelectTrigger>
                <SelectContent>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}{m.isOwner ? " (you)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {team.filter((m) => m.id !== assignee).length > 0 && (
              <div>
                <Label className="text-neutral-600">Also share with</Label>
                <div className="mt-2 space-y-2">
                  {team.filter((m) => m.id !== assignee).map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                      <input type="checkbox" checked={shareWith.includes(m.id)} onChange={() => toggleShare(m.id)} className="accent-[#C1121F]" />
                      {m.name || m.email}{m.isOwner ? " (you)" : ""}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveAssign} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="card-save-assign-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, onC, span, placeholder, testid }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <Label className="text-neutral-600">{label}</Label>
      <Input value={v} onChange={(e) => onC(e.target.value)} placeholder={placeholder}
        className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid={testid} />
    </div>
  );
}
